<template>
  <div class="sf-grid sf-grid-fluid sf-grid-gutter-medium tw-px-44">
    <div class="sf-row sf-below-1">
      <div class="sf-col">
        <div class="sf-level sf-level-between sf-below-0">
          <div class="sf-level-item">
            <span class="sf-font-s sf-font-medium tw-text-sf-gray-dark">
              {{ I18n.t('account_selector_modal.select_linked_company_title') }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="sf-row sf-below-3">
      <div class="sf-col">
        <span class="sf-font-s tw-text-sf-gray-darker">
          {{
            I18n.t('account_selector_modal.select_linked_company_description')
          }}
        </span>
      </div>
    </div>
    <div v-if="effectiveLinkedCompanies.length > 0" class="sf-row">
      <div
        class="sf-col sf-layer-border sf-layer-rounded"
        data-testid="linked-companies"
      >
        <div
          v-for="company in effectiveLinkedCompanies"
          :key="company.id"
          class="sf-row sf-below-0"
          :class="{
            'sf-layer-border-bottom': company.isNotLast,
          }"
        >
          <div
            class="
              sf-col sf-pad-x-1 sf-tab-item sf-level sf-level-between
              tw-border-b-0
            "
            :data-testid="'c_' + company.id"
          >
            <div class="sf-level-item">
              <div class="sf-level">
                <div class="sf-level-item !tw-mr-2">
                  <span
                    class="
                      tw-bg-sf-neutral-subtle-2
                      tw-w-[28px]
                      tw-h-[28px]
                      tw-rounded-[4px]
                      tw-inline-flex
                      tw-items-center
                      tw-justify-center
                    "
                  >
                    <span class="icon icon-link"></span>
                  </span>
                </div>
                <div class="sf-level-item !tw-mr-2">
                  <span class="sf-font-medium sf-pad-r-1 tw-text-sf-gray-dark">
                    {{ truncatedCompanyName(company.name) }}
                  </span>
                  <span
                    v-if="company.periodMissing"
                    v-dompurify-html="
                      I18n.t(
                        'account_selector_modal.missing_external_period_error',
                        { period: ledgerLabel }
                      )
                    "
                    v-tooltip.html="
                      I18n.t(
                        'account_selector_modal.missing_external_period_error_tooltip',
                        { company: company.name }
                      )
                    "
                    class="sf-label sf-label-danger sf-label-active"
                  ></span>
                  <div v-if="company.lastSyncedAt">
                    <i class="icon-refresh sf-font-xs sf-c-text-light"></i>
                    <span class="sf-c-text-light sf-font-xs">
                      {{
                        I18n.t('account_selector_modal.last_synced_on', {
                          date: company.lastSyncedAt,
                        })
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="sf-level-item">
              <SfButton
                :variant="'primary'"
                :small="true"
                @click="
                  () => {
                    emit('company-selected', company as ExternalCompany)
                  }
                "
              >
                <span class="sf-font-xs">
                  {{ I18n.t('account_selector_modal.choose_button_label') }}
                </span>
                <span class="sf-icon icon-arrow-right"></span>
              </SfButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import formatTimestamp from '@app/utils/time-formatter'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { ExternalCompany } from '@app/application/reconciliation/types'
import SfButton from '@app/shared/vue/components/sf-button/sf-button.vue'
import { computed } from 'vue'

interface ExternalCompanySelectorProps {
  linkedCompanies: Array<ExternalCompany>
  linkedCompaniesWithPeriod: Array<number>
  ledgerLabel: string
}

const { linkedCompanies, linkedCompaniesWithPeriod, ledgerLabel } =
  defineProps<ExternalCompanySelectorProps>()

const emit =
  defineEmits<{
    'company-selected': [company: ExternalCompany]
  }>()

const I18n = useI18n()

const effectiveLinkedCompanies = computed(() => {
  return linkedCompanies.map((company, index) => ({
    ...company,
    isNotLast: isNotLastItem(index),
    lastSyncedAt: company.lastSyncedAt
      ? formatDate(company.lastSyncedAt)
      : undefined,
    name: truncatedCompanyName(company.name),
    periodMissing: !linkedCompaniesWithPeriod.includes(company.id),
  }))
})

const isNotLastItem = (index: number) => {
  return index !== linkedCompanies.length - 1
}

const formatDate = (date: string) => {
  return formatTimestamp(date, {
    shouldUseLocalTimezone: true,
    format: 'D MMM Y, HH:mm z',
  })
}

const truncatedCompanyName = (name: string) => {
  const allowedLength = 40

  return name && name.length > allowedLength
    ? `${name.slice(0, allowedLength)}...`
    : name
}
</script>
